/**
 * Admin Project Call Costs Component
 * 
 * Displays cost breakdown by project with daily analysis
 * Shows total costs per day for each project in the system
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  Refresh,
  ExpandMore,
  TrendingUp,
  AttachMoney,
  Assessment,
  BarChart
} from '@mui/icons-material';
import { useProjectBilling } from '../../hooks/useProjectBilling';
import { 
  formatProjectCost, 
  formatProjectCallCount,
  DailyCostData 
} from '../../utils/projectBillingCalculations';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';

interface ProjectCostCardProps {
  title: string;
  totalCost: number;
  totalCalls: number;
  projectCount: number;
}

const ProjectCostCard: React.FC<ProjectCostCardProps> = ({
  title,
  totalCost,
  totalCalls,
  projectCount
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <AttachMoney color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" component="div">
          {title}
        </Typography>
      </Box>
      
      <Typography variant="h4" color="primary" gutterBottom>
        {formatProjectCost(totalCost)}
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Total Calls
          </Typography>
          <Typography variant="h6">
            {formatProjectCallCount(totalCalls)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Projects
          </Typography>
          <Typography variant="h6">
            {projectCount}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

interface DailyCostTableProps {
  dailyCosts: DailyCostData[];
  projectName: string;
}

const DailyCostTable: React.FC<DailyCostTableProps> = ({ dailyCosts, projectName }) => {
  // Filter out days with zero costs for cleaner display
  const nonZeroDays = dailyCosts.filter(day => day.totalCost > 0 || day.calls > 0);
  
  if (nonZeroDays.length === 0) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
        No calls found for {projectName} in this period
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell align="right">Total Cost</TableCell>
            <TableCell align="right">Calls</TableCell>
            <TableCell align="right">Standard</TableCell>
            <TableCell align="right">Premium</TableCell>
            <TableCell align="right">Super Premium</TableCell>
            <TableCell align="right">Reasoning</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {nonZeroDays.map((day) => (
            <TableRow key={day.date} hover>
              <TableCell>
                {new Date(day.date).toLocaleDateString()}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" fontWeight="bold">
                  {formatProjectCost(day.totalCost)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                {day.calls.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                <Box>
                  <Typography variant="caption" display="block">
                    {formatProjectCost(day.standardCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({day.standardCalls} calls)
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box>
                  <Typography variant="caption" display="block">
                    {formatProjectCost(day.premiumCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({day.premiumCalls} calls)
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box>
                  <Typography variant="caption" display="block">
                    {formatProjectCost(day.superPremiumCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({day.superPremiumCalls} calls)
                  </Typography>
                </Box>
              </TableCell>
              <TableCell align="right">
                <Box>
                  <Typography variant="caption" display="block">
                    {formatProjectCost(day.reasoningCost)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({day.reasoningCalls} calls)
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const AdminProjectCosts: React.FC = () => {
  const [expandedProject, setExpandedProject] = useState<string | null>(null);
  
  const {
    data: billingData,
    currentPeriod,
    isLoading,
    isError,
    error,
    isFetching,
    selectedPeriodType,
    availablePeriodTypes,
    setPeriodType,
    validationErrors,
    refetch
  } = useProjectBilling({
    usePreview: true // Use preview for better performance
  });

  if (validationErrors.length > 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Validation Errors</Typography>
          {validationErrors.map((error, index) => (
            <Typography key={index}>{error}</Typography>
          ))}
        </Alert>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Project Cost Data</Typography>
          <Typography>{error?.message || 'Unknown error occurred'}</Typography>
        </Alert>
      </Box>
    );
  }

  // Prepare data for DataGrid
  const projectRows = billingData?.projects.map(project => ({
    id: project.projectId,
    projectName: project.projectName,
    totalCost: project.totalCost,
    totalCalls: project.totalCalls,
    avgDailyCost: project.totalCost / Math.max(1, project.dailyCosts.length),
    avgDailyCalls: project.totalCalls / Math.max(1, project.dailyCosts.length),
    activeDays: project.dailyCosts.filter(day => day.calls > 0).length,
    lastActivity: project.dailyCosts.filter(day => day.calls > 0).pop()?.date || 'No activity'
  })) || [];

  const columns: GridColDef[] = [
    { 
      field: 'projectName', 
      headerName: 'Project Name', 
      width: 200,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold">
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'totalCost', 
      headerName: 'Total Cost', 
      width: 120,
      align: 'right',
      renderCell: (params) => (
        <Typography variant="body2" color="primary" fontWeight="bold">
          {formatProjectCost(params.value)}
        </Typography>
      )
    },
    { 
      field: 'totalCalls', 
      headerName: 'Total Calls', 
      width: 120,
      align: 'right',
      renderCell: (params) => formatProjectCallCount(params.value)
    },
    { 
      field: 'avgDailyCost', 
      headerName: 'Avg Daily Cost', 
      width: 130,
      align: 'right',
      renderCell: (params) => formatProjectCost(params.value)
    },
    { 
      field: 'avgDailyCalls', 
      headerName: 'Avg Daily Calls', 
      width: 130,
      align: 'right',
      renderCell: (params) => Math.round(params.value).toLocaleString()
    },
    { 
      field: 'activeDays', 
      headerName: 'Active Days', 
      width: 110,
      align: 'right'
    },
    { 
      field: 'lastActivity', 
      headerName: 'Last Activity', 
      width: 130,
      renderCell: (params) => {
        if (params.value === 'No activity') {
          return <Chip label="No activity" size="small" color="default" />;
        }
        return new Date(params.value).toLocaleDateString();
      }
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
          Project Call Costs
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Cost analysis across all projects in the system
        </Typography>
      </Box>

      {/* Period Selector */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={9}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Time Period</InputLabel>
              <Select
                value={selectedPeriodType}
                onChange={(e) => setPeriodType(e.target.value)}
                label="Time Period"
                disabled={isLoading}
              >
                {availablePeriodTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => refetch()}
              disabled={isLoading}
              fullWidth
              sx={{ height: 56 }} // Match the height of the Select component
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Loading Indicator */}
      {(isLoading || isFetching) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {isLoading ? 'Loading project cost data...' : 'Refreshing data...'}
          </Typography>
        </Box>
      )}

      {/* Main Content */}
      {billingData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <ProjectCostCard
                title="Total Cost (All Projects)"
                totalCost={billingData.totalCostAllProjects}
                totalCalls={billingData.totalCallsAllProjects}
                projectCount={billingData.projects.length}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Assessment color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Period Summary
                    </Typography>
                  </Box>
                  <Typography variant="body1" gutterBottom>
                    {currentPeriod.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentPeriod.startDate.toLocaleDateString()} - {currentPeriod.endDate.toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {currentPeriod.isPartial ? 'Partial period' : 'Complete period'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TrendingUp color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Top Project
                    </Typography>
                  </Box>
                  {billingData.projects.length > 0 && (
                    <>
                      <Typography variant="body1" gutterBottom>
                        {billingData.projects.sort((a, b) => b.totalCost - a.totalCost)[0].projectName}
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {formatProjectCost(billingData.projects.sort((a, b) => b.totalCost - a.totalCost)[0].totalCost)}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Projects Table */}
          <Paper sx={{ mb: 4 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Project Cost Breakdown
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click on any row in the table above to view daily cost breakdown for that project
              </Typography>
              <DataGrid
                rows={projectRows}
                columns={columns}
                autoHeight
                slots={{ toolbar: GridToolbar }}
                slotProps={{ 
                  toolbar: { 
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 }
                  } 
                }}
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'totalCost', sort: 'desc' }],
                  },
                }}
                disableRowSelectionOnClick={false}
                onRowClick={(params) => {
                  setExpandedProject(params.row.id);
                  // Scroll to the accordion section
                  setTimeout(() => {
                    const accordionElement = document.getElementById(`project-accordion-${params.row.id}`);
                    if (accordionElement) {
                      accordionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }, 100);
                }}
                rowSelectionModel={expandedProject ? [expandedProject] : []}
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer',
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
              />
            </Box>
          </Paper>

          {/* Daily Breakdown Accordions */}
          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Daily Cost Breakdown by Project
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click on a project to see daily cost details
              </Typography>
              
              {billingData.projects
                .filter(project => project.totalCost > 0) // Only show projects with costs
                .sort((a, b) => b.totalCost - a.totalCost) // Sort by total cost descending
                .map((project) => (
                <Accordion 
                  key={project.projectId}
                  id={`project-accordion-${project.projectId}`}
                  expanded={expandedProject === project.projectId}
                  onChange={() => setExpandedProject(
                    expandedProject === project.projectId ? null : project.projectId
                  )}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        {project.projectName}
                      </Typography>
                      <Typography variant="body2" color="primary" sx={{ mr: 2 }}>
                        {formatProjectCost(project.totalCost)}
                      </Typography>
                      <Chip 
                        label={`${formatProjectCallCount(project.totalCalls)} calls`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DailyCostTable 
                      dailyCosts={project.dailyCosts}
                      projectName={project.projectName}
                    />
                  </AccordionDetails>
                </Accordion>
              ))}
              
              {billingData.projects.filter(project => project.totalCost > 0).length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No projects with costs found for this period
                </Typography>
              )}
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default AdminProjectCosts;
